/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { dispatch, UserStore, useUserSubprofilesSelector } from "@nf/common";
import React, { useEffect } from "react";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

import ActionsHandler from "components/ActionsHandler";
import type {
  IAddProfileProps,
  IProfileCircleProps,
} from "components/Subprofiles";

import styles from "./SettingsProfiles.module.scss";

const ParentalControl = dynamic(() => import("./components/ParentalControl"));

const AddProfile = dynamic<IAddProfileProps>(() =>
  import("components/Subprofiles/ProfileCircle").then((mod) => mod.AddProfile)
);

const ProfileCircle = dynamic<IProfileCircleProps>(() =>
  import("components/Subprofiles/ProfileCircle").then(
    (mod) => mod.ProfileCircle
  )
);

export const SettingsProfiles = () => {
  const { t } = useTranslation("translations");

  const {
    Data: subprofilesState,
    IsProcessing: profilesLoading,
    Error: profilesError,
  } = useUserSubprofilesSelector();

  const subprofilesList = subprofilesState?.subprofiles;
  const currentSubprofile = subprofilesState?.currentSubprofile;
  const pinCodeState = subprofilesState?.auth;

  const isKidProfileAvailable = subprofilesState?.subprofiles?.some(
    (profile) => profile.Child
  );

  useEffect(() => {
    !subprofilesList?.length &&
      !profilesError &&
      !profilesLoading &&
      dispatch(UserStore.Actions.getUserSubprofiles());
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      <p>{t("web_subprofiles_description")}</p>
      <ActionsHandler
        isLoading={profilesLoading}
        error={profilesError || pinCodeState?.Error}
      >
        <>
          <div className={styles.profileList}>
            {subprofilesList?.map((subprofile) => (
              <ProfileCircle
                editButton={subprofilesState?.currentSubprofile?.Owner}
                subprofile={subprofile}
                key={`profile-circle-${subprofile.UserId}`}
                ellipseText
              />
            ))}
            <AddProfile subprofilesState={subprofilesState} />
          </div>
          {isKidProfileAvailable && currentSubprofile?.Owner && (
            <ParentalControl pinCodeState={pinCodeState} />
          )}
        </>
      </ActionsHandler>
    </div>
  );
};

export default SettingsProfiles;
